import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import categoryApi from '../../api/category-api';

const chipStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 6px 4px 14px',
  border: '1px solid #d9d9d9',
  borderRadius: 999,
  background: '#fff',
};

// One logical category can exist as several documents (one per language),
// sharing `translations.sr` — see the API's getCategoryFilterOptions. Keep one
// per group, preferring the Serbian one, then order like the mobile app does:
// homeOrder first, unordered (null) after, in the API's existing order.
const toHomeChips = (categories) => {
  const byCanonicalId = new Map();
  for (const c of categories) {
    if (!c.showOnHome) continue;
    const id = c.translations?.sr ?? c.wooCommerceId;
    if (!byCanonicalId.has(id) || c.lang === 'sr') byCanonicalId.set(id, c);
  }
  return [...byCanonicalId.values()].sort(
    (a, b) => (a.homeOrder ?? Infinity) - (b.homeOrder ?? Infinity) || 0
  );
};

export default function HomeCategoryOrder({ categories, onSaved }) {
  const [chips, setChips] = useState(() => toHomeChips(categories));
  const [saving, setSaving] = useState(false);

  useEffect(() => setChips(toHomeChips(categories)), [categories]);

  const dirty = chips.some((c, i) => c.homeOrder !== i + 1);

  const move = (index, delta) =>
    setChips((prev) => {
      const next = [...prev];
      [next[index], next[index + delta]] = [next[index + delta], next[index]];
      return next;
    });

  const handleSave = async () => {
    setSaving(true);
    const results = await Promise.all(
      chips
        .map((c, i) => ({ c, order: i + 1 }))
        .filter(({ c, order }) => c.homeOrder !== order)
        .map(({ c, order }) => categoryApi.update(c._id, { homeOrder: order }))
    );
    setSaving(false);
    const failed = results.find((r) => !r.success);
    if (failed) {
      toast.error(failed.message || 'Greška pri čuvanju redosleda');
    } else {
      toast.success('Redosled je sačuvan');
    }
    onSaved();
  };

  return (
    <div
      style={{
        padding: 16,
        border: '1px solid #f0f0f0',
        borderRadius: 12,
        background: '#fafafa',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Redosled na početnoj prodavnice
      </div>
      <div style={{ color: '#8c8c8c', fontSize: 13, marginBottom: 12 }}>
        Ovim redom se kategorije prikazuju u aplikaciji. „Svi“ je uvek prvi.
        Kategorija se dodaje uključivanjem „Prikaži na početnoj prodavnice“ u
        njenoj formi.
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span
          style={{
            ...chipStyle,
            padding: '4px 14px',
            borderStyle: 'dashed',
            color: '#8c8c8c',
          }}
        >
          Svi
        </span>
        {chips.map((c, i) => (
          <span key={c._id} style={chipStyle}>
            <span style={{ color: '#8c8c8c' }}>{i + 1}</span>
            <span style={{ marginRight: 4 }}>{c.name}</span>
            <Button
              size="small"
              type="text"
              icon={<LeftOutlined />}
              disabled={i === 0}
              onClick={() => move(i, -1)}
              aria-label={`Pomeri ${c.name} levo`}
            />
            <Button
              size="small"
              type="text"
              icon={<RightOutlined />}
              disabled={i === chips.length - 1}
              onClick={() => move(i, 1)}
              aria-label={`Pomeri ${c.name} desno`}
            />
          </span>
        ))}
        {chips.length === 0 && (
          <span style={{ color: '#8c8c8c' }}>
            Nijedna kategorija nije uključena za prikaz na početnoj.
          </span>
        )}
        <Button
          className="btn-primary"
          type="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!dirty}
          loading={saving}
          onClick={handleSave}
        >
          Sačuvaj redosled
        </Button>
      </div>
    </div>
  );
}

HomeCategoryOrder.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSaved: PropTypes.func.isRequired,
};
